import { Button, Group, Modal, Stack, Text, Title } from '@mantine/core'
import { useDisclosure } from '@mantine/hooks'
import { IconCreditCard } from '@tabler/icons-react'
import { useMutation, useQueryClient } from '@tanstack/react-query'
import { useState } from 'react'

import { StripeProvider } from '@/components/StripeProvider'
import { useGlobalState } from '@/hooks/useGlobalState'
import { usePaymentMethodRemoval } from '@/hooks/usePaymentMethodRemoval'
import { usePaymentMethodUpdate } from '@/hooks/usePaymentMethodUpdate'
import type { PaymentMethod } from '@/services/api/api.types'

import { PaymentMethodDisplay } from './PaymentMethodDisplay'
import { PaymentMethodUpdateModal } from './PaymentMethodUpdateModal'

interface PaymentMethodsSectionProps {
  paymentMethod: PaymentMethod | null
  isLoading?: boolean
}

export function PaymentMethodsSection({ paymentMethod, isLoading = false }: PaymentMethodsSectionProps) {
  const { api } = useGlobalState()
  const queryClient = useQueryClient()
  const [opened, { open, close }] = useDisclosure(false)
  const [confirmDeleteOpened, { open: openConfirmDelete, close: closeConfirmDelete }] = useDisclosure(false)
  const [isAddingNew, setIsAddingNew] = useState(false)
  const [setupClientSecret, setSetupClientSecret] = useState<string | null>(null)

  // Generate Setup Intent for adding/updating payment methods
  const changePaymentMethodMutation = useMutation({
    mutationFn: () => api.changePaymentMethod(),
    onSuccess: response => {
      setSetupClientSecret(response.clientSecret)
      open()
    },
    onError: error => {
      console.error('Failed to setup payment method update:', error)
      // Error handling could be improved with component state if needed
    },
  })

  // Payment method removal with polling
  const { removePaymentMethod, isRemoving } = usePaymentMethodRemoval({
    onRemovalComplete: () => {
      queryClient.invalidateQueries({ queryKey: ['billing', 'info'] })
      closeConfirmDelete()
    },
  })

  // Payment method update with polling
  const { handlePaymentMethodUpdateSuccess, isPolling } = usePaymentMethodUpdate({
    onUpdateComplete: () => {
      queryClient.invalidateQueries({ queryKey: ['billing', 'info'] })
      close()
      setSetupClientSecret(null)
      setIsAddingNew(false)
    },
  })

  const handleUpdatePaymentMethod = () => {
    setIsAddingNew(false)
    changePaymentMethodMutation.mutate()
  }

  const handleAddPaymentMethod = () => {
    setIsAddingNew(true)
    changePaymentMethodMutation.mutate()
  }

  const handleRemove = () => {
    openConfirmDelete()
  }

  const handleConfirmRemove = () => {
    removePaymentMethod()
  }

  const handlePaymentMethodSuccess = () => {
    handlePaymentMethodUpdateSuccess(isAddingNew, paymentMethod)
  }

  const handleModalClose = () => {
    close()
    setSetupClientSecret(null)
    setIsAddingNew(false)
  }

  if (isLoading) {
    return (
      <Stack gap="md">
        <Group justify="space-between" align="center">
          <Title order={3}>Payment Method</Title>
        </Group>
        <Text c="dimmed">Loading payment methods...</Text>
      </Stack>
    )
  }

  return (
    <>
      <Stack gap="md">
        {!paymentMethod ? (
          <>
            <Group py="lg">
              <IconCreditCard size={48} color="var(--mantine-color-gray-5)" />
              <Text fw={500}>No Payment Method</Text>
            </Group>
            <Button onClick={handleAddPaymentMethod} loading={changePaymentMethodMutation.isPending} w="fit-content">
              + Add Payment Method
            </Button>
          </>
        ) : (
          <PaymentMethodDisplay
            paymentMethod={paymentMethod}
            onUpdate={handleUpdatePaymentMethod}
            onRemove={handleRemove}
          />
        )}
      </Stack>

      {/* Payment Method Update Modal */}
      {setupClientSecret && (
        <StripeProvider clientSecret={setupClientSecret}>
          <PaymentMethodUpdateModal
            opened={opened}
            onClose={handleModalClose}
            onSuccess={handlePaymentMethodSuccess}
            clientSecret={setupClientSecret}
            isExistingMethod={!isAddingNew}
            isPolling={isPolling}
          />
        </StripeProvider>
      )}

      {/* Confirm Delete Modal */}
      <Modal opened={confirmDeleteOpened} onClose={closeConfirmDelete} title="Remove Payment Method" size="lg" centered>
        <Stack gap="xl">
          <Text size="md">Are you sure you want to remove this payment method?</Text>

          <Group justify="flex-end" gap="sm" mt="lg">
            <Button variant="default" onClick={closeConfirmDelete} disabled={isRemoving}>
              Cancel
            </Button>
            <Button color="red" onClick={handleConfirmRemove} loading={isRemoving}>
              Remove Payment Method
            </Button>
          </Group>
        </Stack>
      </Modal>
    </>
  )
}
